// Service Worker registration and update handling

export interface ServiceWorkerConfig {
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onError?: (error: Error) => void;
}

export async function registerServiceWorker(config: ServiceWorkerConfig = {}) {
  if (!('serviceWorker' in navigator)) {
    console.log('Service Workers not supported');
    return;
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/'
    });

    console.log('Service Worker registered:', registration);

    // Check for updates every hour
    setInterval(() => {
      registration.update();
    }, 60 * 60 * 1000);

    // Handle updates
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      if (!newWorker) return;

      newWorker.addEventListener('statechange', () => {
        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
          // New service worker available
          console.log('New Service Worker available');
          if (config.onUpdate) {
            config.onUpdate(registration);
          }
        } else if (newWorker.state === 'activated' && !navigator.serviceWorker.controller) {
          // First install
          console.log('Service Worker activated for the first time');
          if (config.onSuccess) {
            config.onSuccess(registration);
          }
        }
      });
    });

    // Handle controller change (new SW activated)
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      console.log('Service Worker controller changed');
      // Could auto-reload here, but better to ask user
    });

    return registration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    if (config.onError) {
      config.onError(error as Error);
    }
  }
}

export function unregisterServiceWorker() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready.then((registration) => {
      registration.unregister();
    });
  }
}

// Skip waiting and activate new service worker
export async function updateServiceWorker(registration: ServiceWorkerRegistration) {
  if (registration.waiting) {
    // Tell SW to skip waiting
    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    
    // Reload once the new SW is controlling the page
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      window.location.reload();
    });
  }
}

// Precache images for offline viewing
export function precacheImages(imageUrls: string[]) {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: 'CACHE_IMAGES',
      urls: imageUrls
    });
  }
}

// Check if app can be installed as PWA
export function checkInstallability(): Promise<boolean> {
  return new Promise((resolve) => {
    if (!window.matchMedia('(display-mode: standalone)').matches) {
      // Not installed yet
      window.addEventListener('beforeinstallprompt', () => {
        resolve(true);
      });
      
      // Set a timeout in case the event doesn't fire
      setTimeout(() => resolve(false), 1000);
    } else {
      // Already installed
      resolve(false);
    }
  });
}